import { consoleLoggerFactory, getDefaultLoggerOptions, type ILogger, LogLevels } from "@/types/console-logger";
const logger: ILogger = consoleLoggerFactory.create( getDefaultLoggerOptions( LogLevels.ERROR ) );

import { cloneDeep } from "lodash-es";

import { DateTime } from "luxon";
import { bonusReports, syncState } from "@/plugins/LocalForagePlugin";
import { bonusReportsSyncKey } from "@/types/products/product-definitions";
import type {
    BonusReport,
    BonusReportsMetaData,
    Category,
    ContentfulBonusReport,
    HasMetaData,
    Product,
    Tag
} from "@tmi/types/entities";
import type { BonusReports } from "@tmi/types/api/content-models";
import { goFetch } from "@/utils/network-utils";
import type { PersistedQueryPayload } from "@tmi/types/api/fetch";
import { CompositionContext } from "@/services/data/sync-supervisor";

export class BonusReportsSyncCoordinator {
    readonly #compositionContext: CompositionContext;

    constructor( ctx: CompositionContext ) {
        this.#compositionContext = ctx;
    }

    public async patchRepository( authToken: string ): Promise< number > {
        let itemsUpdated = 0;

        try {
            const syncThresholdInMillis = await syncState.getItem< number >( bonusReportsSyncKey );
            if ( null == syncThresholdInMillis || syncThresholdInMillis < DateTime.utc().toMillis() )
            {

                const nextSyncThresholdInMillis = DateTime.utc().plus( { minutes: 5 } ).toMillis();
                await syncState.setItem( bonusReportsSyncKey, nextSyncThresholdInMillis );

            logger.debug( `[ bonus-reports-sync-coordinator ] load all available bonus reports meta-data...` );
            const t0: number = performance.now();

            // Retrieve the latest meta-data for bonus reports
            const allMetadata = await this.refreshMetaDataFromServer( authToken );

            if(0 === allMetadata.length)
            {
                const nextSyncThresholdInMillis = DateTime.utc().plus( { seconds: 15 } ).toMillis();
                await syncState.setItem( bonusReportsSyncKey, nextSyncThresholdInMillis );

                return itemsUpdated;
            }

            const allMetaDataByIdMap = new Map< string, BonusReportsMetaData >(
                allMetadata.map( ( meta: HasMetaData ) => [ meta.contentfulId, meta ] )
            );

            if ( await bonusReports.length() > 0 ) {
                // Iterate over all elements within IDB
                await bonusReports.iterate( ( content: BonusReport, key: string ) => {
                    // If the bonusReports in IDB matches one in the meta-data repository
                    const meta: BonusReportsMetaData | undefined = allMetaDataByIdMap.get( key );
                    // If the meta-data collection has the same bonus report content item that shares the same contentful id...
                    if ( typeof meta !== 'undefined' ) {
                        // If the updated date for both the meta-data and the local bonus report are the same...
                        if ( 0 === meta.updatedDate?.localeCompare( content.updatedDate ?? '' ) ) {
                            // Remove it from the list of available meta-data items
                            allMetaDataByIdMap.delete( key );
                        }
                        // Otherwise, the local content item is out of date and should be replaced
                        else {
                            // Delete the content item from the IDB collection
                            bonusReports.removeItem( key ).then();
                        }
                    }
                    // Else, the current meta-data no longer contains the same content item and thus it should be removed from IDB
                    else {
                        // Delete the content item from the IDB collection
                        bonusReports.removeItem( key ).then();
                    }

                    return;
                } );
            }

            // Transform metadata map into an array of content ids
            const missingIds = [ ...Array.from( allMetaDataByIdMap.keys() ) ];
            // If any content is missing...
            if ( missingIds.length > 0 ) {
                // Fetch it from the server
                itemsUpdated = await this.refreshContentFromServer( authToken, missingIds );
            }

            const t1: number = performance.now();
            logger.info( `[ bonus-reports-sync-coordinator ] Processing all missing bonus reports took ${ t1 - t0 } ms` );
        }
        } catch ( err: any ) {
            logger.error( JSON.stringify( err.message ) );
        }

        return itemsUpdated;
    }

    private async refreshMetaDataFromServer( authToken: string ): Promise< BonusReportsMetaData[] > {
        let page: number = 0;
        const pageSize: number = 5000;
        let consecutiveFailures: number = 0;
        const maxConsecutiveFailures: number = 2;

        const metadata: BonusReportsMetaData[] | undefined = [];
        let metaPayload: ContentfulBonusReport[] | void = [];

        do {
            const response =
                await goFetch< BonusReports, PersistedQueryPayload >( {
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: '15f93c2aca36c4868db8e38ba5be5f4e',
                        variables: {
                            skip: page,
                            limit: pageSize
                        }
                    },
                    authToken: authToken
                } );

            // If no response (API failed), increment failure counter and check if we should stop
            if (!response) {
                consecutiveFailures++;
                logger.warn(`Bonus report metadata fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

                if (consecutiveFailures >= maxConsecutiveFailures) {
                    logger.warn('Too many consecutive failures, stopping bonus report metadata pagination');
                    break;
                }

                page++;
                continue;
            }

            consecutiveFailures = 0;

            metaPayload = response?.data?.bonusReportCatalog?.bonus_reports;
            if ( null != metaPayload ) {
                metaPayload.map( ( a: ContentfulBonusReport ) => {
                    metadata.push( ( {
                        contentfulId: a.id,
                        slug: a.slug,
                        updatedDate: a.publish_at
                    } ) );
                } );
            }

            // update the page #
            page++;
        } while ( (metaPayload?.length ?? 0) >= pageSize );

        return metadata;
    }

    private async refreshContentFromServer( authToken: string, missingContentIds: string[] ): Promise< number > {
        // Timing - testing to see how expensive each method is
        const t0: number = performance.now();
        let itemsUpdated = 0;

        let page: number = 0;
        const pageSize: number = 350;
        let consecutiveFailures: number = 0;
        const maxConsecutiveFailures: number = 2;

        let contentPayload: ContentfulBonusReport[] | undefined = [];
        do {
            const response =
                await goFetch< BonusReports, PersistedQueryPayload >( {
                    endpoint: `${ import.meta.env.VITE_CONTENT_REL_PATH }`,
                    payload: {
                        id: 'dcb63ca81a12007ee8b590e75cd82457',
                        variables: {
                            skip: page,
                            limit: pageSize,
                            contentFilter: {
                                ids: missingContentIds
                            }
                        }
                    },
                    authToken: authToken
                } );

            // If no response (API failed), increment failure counter and check if we should stop
            if (!response) {
                consecutiveFailures++;
                logger.warn(`Bonus report content fetch failed (${consecutiveFailures}/${maxConsecutiveFailures} consecutive failures)`);

                if (consecutiveFailures >= maxConsecutiveFailures) {
                    logger.warn('Too many consecutive failures, stopping bonus report content pagination');
                    break;
                }

                page++;
                continue;
            }

            consecutiveFailures = 0;

            contentPayload = response?.data?.bonusReportCatalog?.bonus_reports;
            if ( null != contentPayload ) {
                const allReports: BonusReport[] = [];
                contentPayload.map( ( r: ContentfulBonusReport ): void => {
                    const report: BonusReport | undefined = composeBonusReport( r, this.#compositionContext );
                    if ( typeof report !== 'undefined' ) {
                        allReports.push( report );
                    }
                } );

                // Store the items recently retrieved
                await bonusReports.ready();
                try {
                    for ( const origReport of allReports ) {
                        await bonusReports.setItem( origReport.contentfulId, cloneDeep( origReport ) );
                    }
                } catch ( err: any ) {
                    logger.error( JSON.stringify( err.message ) );
                }

                const t1: number = performance.now();
                logger.info( `[ bonus-report-sync-coordinator ] Processing ${ allReports.length } bonus reports took ${ t1 - t0 } ms` );

                itemsUpdated += allReports.length;
            }

            // update the page #
            page++;
        } while ( (contentPayload?.length ?? 0) >= pageSize );

        return itemsUpdated;
    }
}

export function composeBonusReport( report: ContentfulBonusReport, ctx: CompositionContext ): BonusReport {
    let hydratedReport = {} as BonusReport;
    try {
        // Categories
        const categories: Category[] = [];
        report.assoc_cat_ids?.forEach( ( id: string ) => {
            const category: Category | undefined = ctx.getCategoryById( id );

            if ( category ) {
                categories.push( cloneDeep( category ) );
            }
        } );

        // Products
        const products: Product[] = [];
        report.assoc_product_ids?.forEach( ( id: string ) => {
            const product: Product | undefined = ctx.getProductById( id );

            if ( product ) {
                products.push( cloneDeep( product ) );
            } else {
                logger.warn( `The given product id '${ id }' was not found to match a known Product content item.` );
            }
        } );

        // Tags
        const tags: Tag[] = [];
        report.assoc_tag_ids?.forEach( ( id: string ): void => {
            const tag: Tag | undefined = ctx.getTagById( id );

            if ( tag ) {
                tags.push( tag );
            }
        } );

        hydratedReport = ( {
            contentfulId: report.id,
            publishedDate: report.pub_dt,

            updatedDate: report.publish_at,

            slug: report.slug ?? undefined,
            title: report.name ?? undefined,

            pdfLink: report.pdf_uri ?? undefined,
            thumbnail: report.thumbnail_uri ?? undefined,

            categories: categories,
            products: products,
            tags: cloneDeep( tags ),

            adProduct: undefined,
            author: undefined,
            body2: undefined,
            editorialAd: undefined,
            preview: undefined,
            text: undefined,
            videoLink: undefined,
            industrySectors: [],
            sectors: [],
            tickers: []
        } );
    } catch ( e: any ) {
        logger.error( JSON.stringify( e.message ) );
    }

    return hydratedReport;
}
